/**
 * EvolutionGraph — Gource-style animated graph for the Evolution tab.
 *
 * Unlike OntologyGraph (which rebuilds from scratch), this component:
 *  - Keeps ONE persistent graph (sigma's own graph, mutated in-place)
 *  - Runs FA2 as a continuous web-worker for organic settling
 *  - Animates node fade-in / fade-out / size-grow via nodeReducer + RAF
 *  - Staggers new node appearances (one every STAGGER_MS) so viewers
 *    can track each addition (Gource-style)
 *
 * Usage:
 *   const ref = useRef<EvolutionGraphHandle>(null);
 *   <EvolutionGraph ref={ref} onNodeClick={...} />
 *   await ref.current.initGraph(graph);
 *   await ref.current.transitionTo(newGraph, diff);
 */
import {
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { SigmaContainer, useSigma, useCamera } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import { MultiDirectedGraph } from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import type { OntologyGraph, OntologyDiff } from '@/services/api';
import { ENTITY_TYPE_COLORS, REL_TYPE_COLORS } from './OntologyGraph';

// ─── Tuneable constants ────────────────────────────────────────────────────────
const STAGGER_MS      = 180;  // delay between each added node appearing
const FADE_IN_MS      = 500;  // time for new nodes to grow/fade in
const FADE_OUT_MS     = 600;  // time for removed nodes to fade out before drop
const PULSE_SCALE     = 2.2;  // new nodes briefly burst to this multiplier
const PULSE_DECAY_MS  = 300;  // then ease back to normal size
const FA2_RESTART_MS  = 4000; // how long FA2 runs after a transition
const BG_COLOR        = '#0f172a';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function lerpColor(from: string, to: string, t: number): string {
  const hex = (s: string) => [
    parseInt(s.slice(1, 3), 16),
    parseInt(s.slice(3, 5), 16),
    parseInt(s.slice(5, 7), 16),
  ];
  const [r1, g1, b1] = hex(from);
  const [r2, g2, b2] = hex(to);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function nodeTargetColor(entityType: string, isAdded: boolean, isRemoved: boolean): string {
  if (isAdded)   return '#22c55e';
  if (isRemoved) return '#ef4444';
  return ENTITY_TYPE_COLORS[entityType] ?? '#94a3b8';
}

function nodeTargetSize(isAdded: boolean): number {
  return isAdded ? 9 : 6;
}

// ─── Animation state (lives outside React to avoid re-renders) ───────────────
interface NodeAnim {
  phase: 'in' | 'out' | 'pulse-decay';
  startTime: number;
  duration: number;
  fromColor: string;
  toColor: string;
  fromSize: number;
  toSize: number;
  baseSize: number; // the settled size (pulse returns here)
}

// ─── Public handle ────────────────────────────────────────────────────────────
export interface EvolutionGraphHandle {
  initGraph: (graph: OntologyGraph) => void;
  /** Transition to a new graph version. onProgress is called with values 0→1 over the full duration. */
  transitionTo: (
    newGraph: OntologyGraph,
    diff: OntologyDiff,
    addedNodeIds: Set<string>,
    removedNodeIds: Set<string>,
    onProgress?: (p: number) => void,
  ) => Promise<void>;
}

// ─── Inner controller (has sigma context) ────────────────────────────────────
function EvolutionGraphController({
  onNodeClick,
  handleRef,
}: {
  onNodeClick?: (nodeId: string) => void;
  handleRef: React.MutableRefObject<EvolutionGraphHandle | null>;
}) {
  const sigma    = useSigma();
  const { reset } = useCamera();
  const fa2Ref   = useRef<FA2Layout | null>(null);
  const fa2StopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef   = useRef<number | null>(null);
  const animMap  = useRef<Map<string, NodeAnim>>(new Map());

  // ── RAF animation loop ────────────────────────────────────────────────────
  const startAnimLoop = useCallback(() => {
    if (rafRef.current !== null) return; // already running

    const tick = () => {
      const now = performance.now();
      const g   = sigma.getGraph();
      let hasActive = false;

      for (const [nodeId, anim] of animMap.current) {
        if (!g.hasNode(nodeId)) {
          animMap.current.delete(nodeId);
          continue;
        }

        const rawT  = Math.min(1, (now - anim.startTime) / anim.duration);
        const t     = anim.phase === 'in' ? easeOutCubic(rawT) : easeInOutQuad(rawT);
        const color = lerpColor(anim.fromColor, anim.toColor, t);
        const size  = anim.fromSize + (anim.toSize - anim.fromSize) * t;

        g.setNodeAttribute(nodeId, '_animColor', color);
        g.setNodeAttribute(nodeId, '_animSize',  size);

        if (rawT >= 1) {
          animMap.current.delete(nodeId);

          if (anim.phase === 'out') {
            // Actually drop the node now
            if (g.hasNode(nodeId)) g.dropNode(nodeId);
          } else if (anim.phase === 'in') {
            // After fade-in, start pulse-decay back to normal size
            if (anim.toSize > anim.baseSize) {
              animMap.current.set(nodeId, {
                phase:     'pulse-decay',
                startTime: now,
                duration:  PULSE_DECAY_MS,
                fromColor: anim.toColor,
                toColor:   anim.toColor,
                fromSize:  anim.toSize,
                toSize:    anim.baseSize,
                baseSize:  anim.baseSize,
              });
              hasActive = true;
            } else {
              g.setNodeAttribute(nodeId, '_animColor', undefined);
              g.setNodeAttribute(nodeId, '_animSize',  undefined);
            }
          } else {
            g.setNodeAttribute(nodeId, '_animColor', undefined);
            g.setNodeAttribute(nodeId, '_animSize',  undefined);
          }
        } else {
          hasActive = true;
        }
      }

      sigma.refresh({ skipIndexation: true });

      if (hasActive) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [sigma]);

  // ── nodeReducer: pick up _animColor / _animSize ───────────────────────────
  useEffect(() => {
    sigma.setSettings({
      nodeReducer: (node, data) => {
        const color = data._animColor as string | undefined;
        const size  = data._animSize  as number | undefined;
        return {
          ...data,
          color: color ?? data.color,
          size:  size  ?? data.size,
        };
      },
    });
    return () => {
      sigma.setSettings({ nodeReducer: undefined });
    };
  }, [sigma]);

  // ── Click events ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sigma) return;
    const handler = ({ node }: { node: string }) => onNodeClick?.(node);
    sigma.on('clickNode', handler);
    return () => { sigma.off('clickNode', handler); };
  }, [sigma, onNodeClick]);

  // ── FA2 helpers ───────────────────────────────────────────────────────────
  const stopFA2 = useCallback(() => {
    if (fa2StopTimer.current) { clearTimeout(fa2StopTimer.current); fa2StopTimer.current = null; }
    if (fa2Ref.current) { fa2Ref.current.stop(); fa2Ref.current.kill(); fa2Ref.current = null; }
  }, []);

  const startFA2 = useCallback((durationMs: number) => {
    stopFA2();
    const g = sigma.getGraph();
    if (g.order === 0) return;

    const fa2 = new FA2Layout(g, {
      settings: {
        gravity:                        0.3,
        scalingRatio:                   6,
        barnesHutOptimize:              g.order > 80,
        barnesHutTheta:                 0.5,
        slowDown:                       8,
        strongGravityMode:              false,
        outboundAttractionDistribution: true,
        linLogMode:                     false,
      },
    });
    fa2Ref.current = fa2;
    fa2.start();

    fa2StopTimer.current = setTimeout(() => {
      if (fa2Ref.current?.isRunning()) fa2Ref.current.stop();
    }, durationMs);
  }, [sigma, stopFA2]);

  // ── Place new node near its neighbors in the current graph ────────────────
  const seedPosition = useCallback((nodeId: string, g: ReturnType<typeof sigma.getGraph>) => {
    const neighbors = g.neighbors(nodeId);
    if (neighbors.length === 0) {
      // Random with some spread
      return { x: (Math.random() - 0.5) * 4, y: (Math.random() - 0.5) * 4 };
    }
    let sx = 0, sy = 0;
    for (const nb of neighbors) {
      if (g.hasNode(nb)) {
        sx += (g.getNodeAttribute(nb, 'x') as number) ?? 0;
        sy += (g.getNodeAttribute(nb, 'y') as number) ?? 0;
      }
    }
    const jitter = 0.3;
    return {
      x: sx / neighbors.length + (Math.random() - 0.5) * jitter,
      y: sy / neighbors.length + (Math.random() - 0.5) * jitter,
    };
  }, [sigma]);

  // ── initGraph: load first graph synchronously then start FA2 ─────────────
  const initGraph = useCallback((graphData: OntologyGraph) => {
    stopFA2();
    animMap.current.clear();
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }

    const sigmaGraph = sigma.getGraph();
    sigmaGraph.clear();

    for (const node of graphData.nodes) {
      const color = ENTITY_TYPE_COLORS[node.entity_type] ?? '#94a3b8';
      if (!sigmaGraph.hasNode(node.id)) {
        sigmaGraph.addNode(node.id, {
          label:       node.name,
          size:        6,
          color,
          entity_type: node.entity_type,
          category:    node.category,
          x:           Math.random(),
          y:           Math.random(),
        });
      }
    }

    const addedEdgeIds = new Set<string>();
    for (const edge of graphData.edges) {
      if (!sigmaGraph.hasNode(edge.source) || !sigmaGraph.hasNode(edge.target)) continue;
      const eid = `${edge.source}__${edge.rel_type}__${edge.target}`;
      if (addedEdgeIds.has(eid)) continue;
      addedEdgeIds.add(eid);
      try {
        sigmaGraph.addDirectedEdgeWithKey(eid, edge.source, edge.target, {
          size:     Math.max(1, Math.min(3, (edge.weight ?? 1) * 1.5)),
          color:    REL_TYPE_COLORS[edge.rel_type] ?? '#334155',
          rel_type: edge.rel_type,
          weight:   edge.weight,
        });
      } catch { /* skip duplicate */ }
    }

    // Synchronous layout to get a stable starting position
    forceAtlas2.assign(sigmaGraph, {
      iterations: 120,
      settings: { gravity: 1, scalingRatio: 8, slowDown: 10 },
    });

    sigma.refresh();
    setTimeout(() => reset({ duration: 400 }), 100);

    // Then hand off to FA2 worker for organic settling
    startFA2(5000);
  }, [sigma, reset, stopFA2, startFA2]);

  // ── transitionTo: Gource-style incremental update ────────────────────────
  const transitionTo = useCallback(async (
    newGraph: OntologyGraph,
    diff: OntologyDiff,
    addedNodeIds: Set<string>,
    removedNodeIds: Set<string>,
    onProgress?: (p: number) => void,
  ): Promise<void> => {
    const now = performance.now();
    const sigmaGraph = sigma.getGraph();

    // 1. Fade out removed nodes
    for (const rn of diff.removed_nodes) {
      if (!sigmaGraph.hasNode(rn.id)) continue;
      const curColor = (sigmaGraph.getNodeAttribute(rn.id, 'color') as string) ?? '#94a3b8';
      const curSize  = (sigmaGraph.getNodeAttribute(rn.id, 'size')  as number) ?? 6;
      sigmaGraph.setNodeAttribute(rn.id, 'color', '#ef4444'); // flash red first
      animMap.current.set(rn.id, {
        phase:     'out',
        startTime: now,
        duration:  FADE_OUT_MS,
        fromColor: '#ef4444',
        toColor:   BG_COLOR,
        fromSize:  curSize,
        toSize:    0,
        baseSize:  0,
      });
    }

    // Remove edges for removed nodes immediately (avoids dangling edges)
    for (const re of diff.removed_edges) {
      const eid = `${re.source}__${re.rel_type}__${re.target}`;
      if (sigmaGraph.hasEdge(eid)) sigmaGraph.dropEdge(eid);
    }

    // 2. Add new edges for existing nodes that already exist in both graphs
    //    (edges connecting non-added nodes)
    const addedEdgeIds = new Set<string>();
    for (const edge of diff.added_edges) {
      if (addedNodeIds.has(edge.source) || addedNodeIds.has(edge.target)) continue; // handle after nodes are added
      if (!sigmaGraph.hasNode(edge.source) || !sigmaGraph.hasNode(edge.target)) continue;
      const eid = `${edge.source}__${edge.rel_type}__${edge.target}`;
      if (addedEdgeIds.has(eid) || sigmaGraph.hasEdge(eid)) continue;
      addedEdgeIds.add(eid);
      try {
        sigmaGraph.addDirectedEdgeWithKey(eid, edge.source, edge.target, {
          size:     Math.max(1, Math.min(3, (edge.weight ?? 1) * 1.5)),
          color:    REL_TYPE_COLORS[edge.rel_type] ?? '#334155',
          rel_type: edge.rel_type,
          weight:   edge.weight,
        });
      } catch { /* skip */ }
    }

    // 3. Start animation loop (covers the fade-out phase and upcoming adds)
    startAnimLoop();

    // 4. Staggered new node additions
    const addedList = diff.added_nodes;
    const totalAddTime = addedList.length * STAGGER_MS;

    // Smooth 0→1 progress reporter — runs a separate RAF and calls onProgress
    // over the full expected transition duration so the scrubber moves fluidly.
    const totalProgressDuration = FADE_OUT_MS * 0.4 + totalAddTime + FADE_IN_MS;
    const progressStart = performance.now();
    let progressDone = false;
    let progressRaf: number | null = null;
    if (onProgress) {
      const reportProgress = () => {
        const elapsed = performance.now() - progressStart;
        const p = Math.min(0.99, elapsed / totalProgressDuration);
        onProgress(p);
        if (!progressDone) progressRaf = requestAnimationFrame(reportProgress);
      };
      progressRaf = requestAnimationFrame(reportProgress);
    }

    await new Promise<void>((resolve) => {
      addedList.forEach((node, i) => {
        setTimeout(() => {
          if (!sigmaGraph.hasNode(node.id)) {
            const targetColor = '#22c55e';
            const targetSize  = nodeTargetSize(true);
            const pos = seedPosition(node.id, sigmaGraph);

            sigmaGraph.addNode(node.id, {
              label:       node.name,
              size:        0,
              color:       BG_COLOR,
              entity_type: node.entity_type,
              category:    node.category,
              x:           pos.x,
              y:           pos.y,
            });

            // Add edges involving this new node
            for (const edge of newGraph.edges) {
              if (edge.source !== node.id && edge.target !== node.id) continue;
              const other = edge.source === node.id ? edge.target : edge.source;
              if (!sigmaGraph.hasNode(other)) continue;
              const eid = `${edge.source}__${edge.rel_type}__${edge.target}`;
              if (addedEdgeIds.has(eid) || sigmaGraph.hasEdge(eid)) continue;
              addedEdgeIds.add(eid);
              try {
                sigmaGraph.addDirectedEdgeWithKey(eid, edge.source, edge.target, {
                  size:     Math.max(1, Math.min(3, (edge.weight ?? 1) * 1.5)),
                  color:    REL_TYPE_COLORS[edge.rel_type] ?? '#334155',
                  rel_type: edge.rel_type,
                  weight:   edge.weight,
                });
              } catch { /* skip */ }
            }

            // Animate: grow + pulse
            const now2 = performance.now();
            animMap.current.set(node.id, {
              phase:     'in',
              startTime: now2,
              duration:  FADE_IN_MS,
              fromColor: BG_COLOR,
              toColor:   targetColor,
              fromSize:  0,
              toSize:    targetSize * PULSE_SCALE, // pulse up, then decay
              baseSize:  targetSize,
            });
            startAnimLoop();
          }

          if (i === addedList.length - 1) resolve();
        }, FADE_OUT_MS * 0.4 + i * STAGGER_MS);
      });

      // If no nodes to add, resolve immediately
      if (addedList.length === 0) resolve();
    });

    // 5. After all nodes added, update colors of unchanged nodes + finish progress
    progressDone = true;
    if (progressRaf !== null) cancelAnimationFrame(progressRaf);
    onProgress?.(1);

    const SETTLE_DELAY = 200;
    setTimeout(() => {
      for (const node of newGraph.nodes) {
        if (!sigmaGraph.hasNode(node.id)) continue;
        if (addedNodeIds.has(node.id)) continue;
        const canonColor = ENTITY_TYPE_COLORS[node.entity_type] ?? '#94a3b8';
        sigmaGraph.setNodeAttribute(node.id, 'color', canonColor);
      }
    }, SETTLE_DELAY);

    // 6. Restart FA2 so graph settles organically after changes
    const restartDelay = Math.max(FADE_OUT_MS, totalAddTime) + 100;
    setTimeout(() => {
      startFA2(FA2_RESTART_MS);
    }, restartDelay);
  }, [sigma, seedPosition, startAnimLoop, startFA2]);

  // ── Expose imperative handle ──────────────────────────────────────────────
  useImperativeHandle(handleRef, () => ({ initGraph, transitionTo }), [initGraph, transitionTo]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopFA2();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [stopFA2]);

  return null;
}

// ─── Public component (wraps SigmaContainer) ─────────────────────────────────
export const EvolutionGraph = forwardRef<
  EvolutionGraphHandle,
  { onNodeClick?: (nodeId: string) => void }
>(function EvolutionGraph({ onNodeClick }, ref) {
  const innerHandleRef = useRef<EvolutionGraphHandle | null>(null);

  // Forward the handle from the inner controller to the outer ref
  useImperativeHandle(ref, () => ({
    initGraph:    (g)             => innerHandleRef.current?.initGraph(g),
    transitionTo: (g, d, a, r, p) => innerHandleRef.current?.transitionTo(g, d, a, r, p) ?? Promise.resolve(),
  }), []);

  const SIGMA_SETTINGS = {
    allowInvalidContainer:         true,
    renderLabels:                  true,
    renderEdgeLabels:              false,
    labelFont:                     'Inter, system-ui, sans-serif',
    labelSize:                     11,
    labelWeight:                   '500',
    labelColor:                    { color: '#e2e8f0' },
    labelDensity:                  0.7,
    labelGridCellSize:             100,
    labelRenderedSizeThreshold:    5,
    stagePadding:                  40,
    defaultNodeType:               'circle',
    defaultEdgeType:               'line',
    zIndex:                        true,
    minCameraRatio:                0.02,
    maxCameraRatio:                20,
    autoRescale:                   true,
    autoCenter:                    true,
  } as const;

  const SIGMA_STYLE = { '--sigma-background-color': BG_COLOR } as React.CSSProperties;

  return (
    <div className="absolute inset-0" style={SIGMA_STYLE}>
      <SigmaContainer
        graph={MultiDirectedGraph}
        settings={SIGMA_SETTINGS}
        style={{ position: 'absolute', inset: 0 }}
      >
        <EvolutionGraphController
          onNodeClick={onNodeClick}
          handleRef={innerHandleRef}
        />
      </SigmaContainer>
    </div>
  );
});
